<template>
  <el-card shadow="never">
    <div slot="header">
      <el-breadcrumb separator-class="el-icon-arrow-right">
        <el-breadcrumb-item :to="{ name: $root.getDashboardPath() }">Dashboard</el-breadcrumb-item>
        <el-breadcrumb-item v-if="facility" :to="{ name: 'Facility Leasing' }">{{String($root.state.facilityData.code).toUpperCase()}} Leasing</el-breadcrumb-item>
        <el-breadcrumb-item v-else :to="{ name: 'Leasing' }">Leasing</el-breadcrumb-item>
      </el-breadcrumb>
    </div>
    <el-tabs v-model="activeTab">
      <el-tab-pane label="Invoice" name="invoice">
        <el-table :data="customerInvoice.lineItems">
          <el-table-column
            label="Date"
          >
            <template slot-scope="scope">
              <span>
                {{ scope.row.createdAt | formatDate }}
              </span>
            </template>
          </el-table-column>
          <el-table-column
            width="300px"
            prop="description"
            label="Description"
          >
          </el-table-column>
          <el-table-column
            label="Due Date"
          >
            <template slot-scope="scope">
              <span v-if="scope.row.dueDate">
                {{ scope.row.dueDate | formatDate }}
              </span>
              <span v-else>
                -
              </span>
            </template>
          </el-table-column>
          <el-table-column
            label="Period"
          >
            <template slot-scope="scope">
              <span>
                {{ getMonthAndYear(scope.row.period) }}
              </span>
            </template>
          </el-table-column>
          <el-table-column
            prop="amount"
            label="Amount"
          >
          </el-table-column>
          <el-table-column
            prop="accountBalance"
            label="Account Balance"
          >
          </el-table-column>
        </el-table>
      </el-tab-pane>
      <el-tab-pane label="Lease Info" name="info">
        <el-descriptions title="Lease Info">
          <el-descriptions-item label="Move In">
            {{ new Date(unitRent.moveIn).toISOString().slice(0, 10) }}
          </el-descriptions-item>
          <el-descriptions-item label="Move Out">
            {{ unitRent.moveOut ? new Date(unitRent.moveOut).toISOString().slice(0, 10) : '-' }}
          </el-descriptions-item>
          <el-descriptions-item v-if="unitRent.updatedPrice" label="Monthly Price">
            $ {{ Number(unitRent.updatedPrice).toFixed(2) }}
          </el-descriptions-item>
          <el-descriptions-item v-else-if="unitRent.pricing" label="Monthly Price">
            $ {{ Number(unitRent.pricing.price).toFixed(2) }}
          </el-descriptions-item>
          <el-descriptions-item label="Lease to">
            {{ customer.email }}
          </el-descriptions-item>
        </el-descriptions>
      </el-tab-pane>
      <el-tab-pane label="Pricing History" name="history">
        <el-table :data="unitRent.history">
          <el-table-column title="Updated By" prop="changedBy"></el-table-column>
          <el-table-column title="Amount" prop="amount"></el-table-column>
        </el-table>
      </el-tab-pane>
      <el-tab-pane v-if="withHeader" label="Customer Info" name="customer">
        <el-descriptions>
        </el-descriptions>
        <el-descriptions title="Customer Info">
          <el-descriptions-item label="Email">
            {{ customer.email }}
          </el-descriptions-item>
          <el-descriptions-item label="Full Name">
            {{ customer.fullName }}
          </el-descriptions-item>
          <el-descriptions-item label="Address">
            {{ customer.address }}
          </el-descriptions-item>
          <el-descriptions-item label="City">
            {{ customer.city }}
          </el-descriptions-item>
          <el-descriptions-item label="State">
            {{ customer.state }}
          </el-descriptions-item>
          <el-descriptions-item label="Zip Code">
            {{ customer.zip }}
          </el-descriptions-item>
          <el-descriptions-item label="Phone Number">
            {{ customer.phone }}
          </el-descriptions-item>
        </el-descriptions>
        <el-divider v-if="customer.business"></el-divider>
        <el-descriptions v-if="customer.business" title="Industry/Business">
          <el-descriptions-item label="Contact">
            {{ customer.business }}
          </el-descriptions-item>
        </el-descriptions>
        <el-divider v-if="customer.alternateContactInformation"></el-divider>
        <el-descriptions
          v-if="customer.alternateContactInformation"
          title="Alternate Contact Information"
        >
          <el-descriptions-item label="Contact">
            {{ customer.alternateContactInformation.contact }}
          </el-descriptions-item>
          <el-descriptions-item
            v-if="customer.alternateContactInformation.phone"
            label="Phone"
          >
            {{ customer.alternateContactInformation.phone }}
          </el-descriptions-item>
        </el-descriptions>
        <el-divider v-if="customer.taxId"></el-divider>
        <el-descriptions v-if="customer.taxId" title="Tax Info">
          <el-descriptions-item label="Tax ID">
            {{ customer.taxId }}
          </el-descriptions-item>
        </el-descriptions>
      </el-tab-pane>
      <el-tab-pane label="Payment Info" name="payment">
        <el-descriptions title="Payment Info">
          <el-descriptions-item v-if="customerInvoice.units.length > 0" label="Total Units booked">
            {{ customerInvoice.units.length }}
          </el-descriptions-item>
          <el-descriptions-item label="Amount Paid">
            ${{ Number(customerInvoice.amountPaid).toFixed(2) }}
          </el-descriptions-item>
          <el-descriptions-item label="Status">
            {{ customerInvoice.status }}
          </el-descriptions-item>
          <el-descriptions-item label="Transactions">
            <el-tag :key="item.transaction" v-for="item of customerInvoice.transactions" size="medium">
              {{item.transaction}}
            </el-tag>
          </el-descriptions-item>
        </el-descriptions>
      </el-tab-pane>
    </el-tabs>
  </el-card>
</template>

<script>
import axios from 'axios';
import { UNIT_RENTAL_API_BASE, CUSTOMER_API_BASE } from '../env';
import getMonth from '../utils/timing';

export default {
  name: 'Lease',
  watch: {
  },
  props: {
    id: { type: String, required: true },
    facility: { type: String },
    withHeader: { type: String, default: () => true },
  },
  async mounted() {
    const response = await axios.get(`${UNIT_RENTAL_API_BASE}/${this.id}`, {
      headers: {
        Authorization: `Bearer ${this.$keycloak.token}`,
      },
    });
    this.unitRent = response.data;
    const customerResponse = await axios.get(`${CUSTOMER_API_BASE}/customers/v1/${response.data.customer}`, {
      headers: {
        Authorization: `Bearer ${this.$keycloak.token}`,
      },
    });
    this.customer = customerResponse.data;
    const customerInvoiceResponse = await axios.get(`${CUSTOMER_API_BASE}/customers/v1/customer/invoice/${response.data.unitInvoice}`, {
      headers: {
        Authorization: `Bearer ${this.$keycloak.token}`,
      },
    });
    this.customerInvoice = customerInvoiceResponse.data;
    this.loading = false;
  },
  data() {
    return { loading: true, customer: null, customerInvoice: null, unitRent: null, activeTab: 'invoice' };
  },
  methods: {
    getMonthAndYear(value) {
      return getMonth(value);
    },
  },
};
</script>

<style scoped>
</style>



// WEBPACK FOOTER //
// src/components/Lease.vue